import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Skeleton } from "../ui/skeleton";
import { isAccountPrimaryRuntime, isTauriRuntime } from "../../lib/runtime";
import { cloudAuthDesktopV2, signInForCurrentRuntime } from "../../services/cloudAuthDesktopV2";
import {
  isCloudOpenAvailable,
  listOwnedCloudBooks,
  type CloudBookSummary,
} from "../../services/openFromCloud";
import {
  cacheBookshelfSummaries,
  readBookshelfSummaries,
} from "../../services/webIdbStore";
import { listBackupBooks, pickBackupFile } from "../../services/desktopBackup";
import { userFacingError } from "../../../shared/userFacingError";

type Props = {
  open: boolean;
  onClose: () => void;
  onSelectBook: (bookId: string) => void;
  openingBookId: string | null;
  onImportBytes?: (bytes: Uint8Array) => Promise<void> | void;
};

function formatUpdatedAt(iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return "";
  return new Date(ms).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

type ShelfRow = CloudBookSummary & { pendingSync?: boolean };

export function BookshelfDialog({ open, onClose, onSelectBook, openingBookId, onImportBytes }: Props) {
  const accountPrimary = isAccountPrimaryRuntime();
  const [books, setBooks] = useState<ShelfRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsSignIn, setNeedsSignIn] = useState(false);
  const [offline, setOffline] = useState(() => typeof navigator !== "undefined" && !navigator.onLine);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const refresh = useCallback(async () => {
    if (!isCloudOpenAvailable()) {
      const cached = await readBookshelfSummaries();
      setBooks(cached ?? []);
      setNeedsSignIn(!cached?.length);
      setError(null);
      return;
    }
    setNeedsSignIn(false);
    setLoading(true);
    setError(null);
    try {
      const online = typeof navigator === "undefined" || navigator.onLine;
      setOffline(!online);
      let rows: ShelfRow[] = [];
      if (online) {
        rows = await listOwnedCloudBooks();
        await cacheBookshelfSummaries(rows);
      } else {
        rows = (await readBookshelfSummaries()) ?? [];
      }
      if (isTauriRuntime()) {
        const backups = await listBackupBooks();
        const known = new Set(rows.map((row) => row.id));
        for (const backup of backups) {
          if (known.has(backup.id)) continue;
          rows.push({
            id: backup.id,
            title: backup.title,
            author: backup.author,
            updatedAt: backup.updatedAt,
            chapterCount: backup.chapterCount,
            pendingSync: true,
          });
        }
      }
      const pending = ((await readBookshelfSummaries()) ?? []).filter((row) => row.pendingSync);
      for (const entry of pending) {
        if (rows.some((row) => row.id === entry.id)) continue;
        rows.push(entry);
      }
      setBooks(rows);
    } catch (e) {
      const cached = await readBookshelfSummaries();
      if (cached?.length) {
        setBooks(cached);
        setError(null);
      } else {
        setError(userFacingError(e, "open"));
        setBooks([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const onSignIn = useCallback(async () => {
    setSigningIn(true);
    setError(null);
    try {
      await signInForCurrentRuntime();
      if (cloudAuthDesktopV2.isSignedIn()) {
        await refresh();
      }
    } catch (e) {
      setError(userFacingError(e, "sign-in"));
    } finally {
      setSigningIn(false);
    }
  }, [refresh]);

  const importBytes = useCallback(
    async (bytes: Uint8Array) => {
      if (!onImportBytes) return;
      setImporting(true);
      setError(null);
      try {
        await onImportBytes(bytes);
      } catch (e) {
        setError(userFacingError(e, "import"));
      } finally {
        setImporting(false);
      }
    },
    [onImportBytes]
  );

  const onImport = useCallback(async () => {
    if (isTauriRuntime()) {
      const bytes = await pickBackupFile();
      if (bytes) await importBytes(bytes);
      return;
    }
    fileInputRef.current?.click();
  }, [importBytes]);

  useEffect(() => {
    if (!open) return;
    void refresh();
  }, [open, refresh]);

  const busy = Boolean(openingBookId) || signingIn || importing;
  const title = accountPrimary ? "Bookshelf" : "Open from cloud";
  const description = accountPrimary
    ? "Books in your Just Write account."
    : "Books in your online library on heyjustwrite.com.";

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !busy) onClose();
      }}
    >
      <DialogContent className="flex max-h-[min(560px,88vh)] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg" showCloseButton={!busy}>
        <DialogHeader className="shrink-0 border-b border-border p-4 text-left">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {needsSignIn ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {accountPrimary
                  ? "Sign in with your Just Write account to browse your bookshelf."
                  : "Sign in with your Just Write account to browse your online library."}
              </p>
              <Button size="sm" onClick={() => void onSignIn()} disabled={busy}>
                {signingIn ? "Connecting…" : "Sign in with Just Write"}
              </Button>
            </div>
          ) : loading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-3/4" />
            </div>
          ) : error ? (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          ) : books.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {accountPrimary
                ? "No books yet. Click New to start one."
                : "No books in your library yet. Save a project while signed in to sync one up."}
            </p>
          ) : (
            <ul className="space-y-1" role="listbox" aria-label={accountPrimary ? "Your bookshelf" : "Your library books"}>
              {books.map((b) => {
                const isOpening = openingBookId === b.id;
                return (
                  <li key={b.id}>
                    <Button
                      type="button"
                      role="option"
                      aria-selected={isOpening}
                      disabled={busy}
                      variant="ghost"
                      className="h-auto w-full items-start justify-between gap-2 px-3 py-2.5 text-left whitespace-normal hover:border-border"
                      onClick={() => onSelectBook(b.id)}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium text-foreground">{b.title || "Untitled"}</span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">
                          {b.author ? `${b.author} · ` : ""}
                          {b.chapterCount} chapter{b.chapterCount === 1 ? "" : "s"}
                          {b.updatedAt ? ` · updated ${formatUpdatedAt(b.updatedAt)}` : ""}
                          {b.pendingSync ? " · Waiting to sync" : ""}
                          {offline && !b.pendingSync ? " · Offline copy" : ""}
                        </span>
                      </span>
                      {isOpening ? <Badge variant="secondary">Opening…</Badge> : null}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <DialogFooter className="mx-0 mb-0 shrink-0 rounded-none border-t sm:justify-end">
          {onImportBytes ? (
            <Button variant="outline" size="sm" onClick={() => void onImport()} disabled={loading || busy}>
              {importing ? "Importing…" : "Import"}
            </Button>
          ) : null}
          <Button variant="outline" size="sm" onClick={() => void refresh()} disabled={loading || busy}>
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
        </DialogFooter>
        <input
          ref={fileInputRef}
          type="file"
          accept=".sbk"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (!file) return;
            void file.arrayBuffer().then((buffer) => importBytes(new Uint8Array(buffer)));
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

/** @deprecated Use BookshelfDialog. Kept so Electron call sites keep compiling during the rename. */
export function OpenFromCloudDialog(props: Props) {
  return <BookshelfDialog {...props} />;
}
